<template>
  <transition name="appointment-confirmation-panel">
    <div v-if="value" class="appointment-confirmation-panel-shell">
      <div
        class="appointment-confirmation-panel-backdrop"
        @click="closePanel"
      />

      <aside class="appointment-confirmation-panel">
        <div class="appointment-confirmation-panel__header">
          <div class="appointment-confirmation-panel__title">
            <span class="appointment-confirmation-panel__icon">
              <i class="fas fa-check-circle" />
            </span>
            <span>Confirmar Cita</span>
          </div>

          <button
            type="button"
            class="appointment-confirmation-panel__close"
            @click="closePanel"
          >
            <i class="fal fa-times-circle" />
          </button>
        </div>

        <div class="appointment-confirmation-panel__body">
          <div class="appointment-confirmation-panel__question">
            ¿Padre, Madre o Tutor asistirá a la reunión?
          </div>
          <custom-radio
            v-model="willAttend"
            name="appointmentConfirmationAttendance"
            class="appointment-confirmation-panel__radio"
            :options="attendanceOptions"
            :error="attendanceError"
            custom-error-msg="Campo requerido"
            @input="handleAttendanceChange"
          />

          <div
            v-if="willAttend === true"
            class="appointment-confirmation-panel__group"
          >
            <div class="appointment-confirmation-panel__question">
              ¿Cuál modalidad asistirá a la reunión?
            </div>
            <custom-radio
              v-model="attendanceResponseId"
              name="appointmentConfirmationModality"
              class="appointment-confirmation-panel__radio"
              :options="modalityOptions"
              :error="modalityError"
              custom-error-msg="Campo requerido"
              @input="modalityError = ''"
            />
          </div>

          <div class="appointment-confirmation-panel__documents">
            <div class="appointment-confirmation-panel__section-title">
              Documento de Invitación Firmada:
            </div>

            <div class="appointment-confirmation-panel__document-box">
              <div class="appointment-confirmation-panel__file-label">
                Archivo:
              </div>

              <custom-file-input
                :key="fileInputKey"
                v-model="signedInvitationFile"
                class="appointment-confirmation-panel__file-input"
                display-style="normal"
                accept=".pdf"
                label=""
                :max-size="5"
                :on-download="() => {}"
                :error="documentError"
                custom-error-msg="El documento de invitación firmada es requerido."
                @input="documentError = ''"
              />
            </div>
          </div>
        </div>

        <div class="appointment-confirmation-panel__footer">
          <action-button
            class="appointment-confirmation-panel__button"
            i="fas fa-times-circle"
            text="Cancelar Cita"
            variant="outline-danger"
            @click="closePanel"
          />
          <action-button
            class="appointment-confirmation-panel__button"
            i="fas fa-save text-white mr-n1 pr-2"
            text="Guardar"
            variant="success"
            :loading="saving"
            :disabled="saving"
            @click="save"
          />
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomFileInput from 'CustomFileInput';
import CustomRadio from '@/components/common/CustomRadio';
import alert from '@/mixin/alert.mixin';
import compuAppointmentService from '@/modules/compu-appointment/api';

const ATTENDANCE_RESPONSE = {
  Presencial: 1,
  LlamadaTelefonica: 2,
  Videollamada: 3,
  AutorizaOtraAgencia: 4,
  SolicitaNuevaFecha: 5,
};

const fallbackResponseLabels = {
  [ATTENDANCE_RESPONSE.Presencial]:
    'Padre, Madre o Tutor asistirá a la reunión de forma presencial.',
  [ATTENDANCE_RESPONSE.LlamadaTelefonica]:
    'Padre, Madre o Tutor asistirá a la reunión a través de llamada telefónica.',
  [ATTENDANCE_RESPONSE.Videollamada]:
    'Padre, Madre o Tutor asistirá a la reunión a través de video llamada.',
  [ATTENDANCE_RESPONSE.AutorizaOtraAgencia]:
    'Padre, Madre o Tutor autorizó que, en el caso de especificarse en esta cita, otra Agencia participe de la reunión.',
  [ATTENDANCE_RESPONSE.SolicitaNuevaFecha]:
    'No podrá asistir a la reunión. Solicitó que se coordine una nueva fecha.',
};

export default {
  name: 'AppointmentConfirmationPanel',
  components: {
    ActionButton,
    CustomFileInput,
    CustomRadio,
  },
  mixins: [alert],
  props: {
    value: { type: Boolean, default: false },
    appointment: { type: Object, default: null },
  },
  data() {
    return {
      responses: [],
      willAttend: null,
      attendanceResponseId: null,
      signedInvitationFile: null,
      fileInputKey: 0,
      attendanceError: '',
      modalityError: '',
      documentError: '',
      saving: false,
    };
  },
  computed: {
    attendanceOptions() {
      return [
        {
          id: 'yes',
          value: true,
          label: 'Sí, asistirá',
        },
        {
          id: 'no',
          value: false,
          label: this.getResponseLabel(ATTENDANCE_RESPONSE.SolicitaNuevaFecha),
        },
      ];
    },
    modalityOptions() {
      return [
        ATTENDANCE_RESPONSE.Presencial,
        ATTENDANCE_RESPONSE.LlamadaTelefonica,
        ATTENDANCE_RESPONSE.Videollamada,
        ATTENDANCE_RESPONSE.AutorizaOtraAgencia,
      ].map((responseId) => ({
        id: responseId,
        value: responseId,
        label: this.getResponseLabel(responseId),
      }));
    },
    selectedAttendanceResponseId() {
      if (this.willAttend === false) {
        return ATTENDANCE_RESPONSE.SolicitaNuevaFecha;
      }

      return this.willAttend === true ? Number(this.attendanceResponseId) : 0;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.resetForm();
        this.fetchResponses();
      }
    },
  },
  methods: {
    async fetchResponses() {
      try {
        const {
          data,
        } = await compuAppointmentService.fetchAttendanceResponses();
        this.responses = Array.isArray(data) ? data : [];
      } catch {
        this.responses = [];
      }
    },
    getResponseLabel(responseId) {
      const response = this.responses.find(
        (item) => Number(item.id) === Number(responseId)
      );

      return response?.name || fallbackResponseLabels[responseId] || '-';
    },
    resetForm() {
      this.willAttend = null;
      this.attendanceResponseId = null;
      this.signedInvitationFile = null;
      this.fileInputKey += 1;
      this.attendanceError = '';
      this.modalityError = '';
      this.documentError = '';
      this.saving = false;
    },
    handleAttendanceChange(value) {
      this.attendanceError = '';

      if (value === false) {
        this.attendanceResponseId = null;
        this.modalityError = '';
      }
    },
    closePanel() {
      if (this.saving) {
        return;
      }

      this.$emit('input', false);
    },
    validate() {
      this.attendanceError = '';
      this.modalityError = '';
      this.documentError = '';

      if (this.willAttend === null) {
        this.attendanceError = 'Campo requerido';
      }

      if (this.willAttend === true && !this.attendanceResponseId) {
        this.modalityError = 'Campo requerido';
      }

      if (!this.signedInvitationFile) {
        this.documentError = 'El documento de invitación firmada es requerido.';
      }

      return (
        !this.attendanceError && !this.modalityError && !this.documentError
      );
    },
    async save() {
      if (!this.validate()) {
        return;
      }

      const formData = new FormData();
      formData.append(
        'attendanceResponseId',
        this.selectedAttendanceResponseId
      );
      formData.append('signedInvitationFile', this.signedInvitationFile);

      try {
        this.saving = true;
        await compuAppointmentService.confirmManuscriptAppointment(
          this.appointment.id,
          formData
        );

        this.ShowToast(
          'Proceso Exitoso',
          'La cita ha sido confirmada exitosamente.',
          'success'
        );
        this.$emit('saved');
        this.$emit('input', false);
      } catch (error) {
        const errorMessage =
          typeof error?.response?.data === 'string'
            ? error.response.data
            : 'No se pudo confirmar la cita.';

        this.ShowToast('Error', errorMessage, 'error');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

.appointment-confirmation-panel-shell {
  position: fixed;
  inset: 0;
  z-index: 1050;
}

.appointment-confirmation-panel-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 30, 32, 0.42);
  backdrop-filter: blur(2px);
}

.appointment-confirmation-panel {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: min(720px, 100vw);
  height: 100vh;
  background: map.get($colors, 'white');
  box-shadow: -12px 0 24px rgba(0, 0, 0, 0.12);
}

.appointment-confirmation-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 26px 12px;
}

.appointment-confirmation-panel__title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #11291b;
}

.appointment-confirmation-panel__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: map.get($colors, 'white');
  background: map.get($colors, 'orange');
  box-shadow: 0 2px 10px rgba(255, 112, 88, 0.25);
}

.appointment-confirmation-panel__close {
  border: 0;
  background: transparent;
  color: map.get($colors, 'orange');
  cursor: pointer;
}

.appointment-confirmation-panel__body {
  flex: 1;
  padding: 10px 26px 22px;
  overflow-y: auto;
}

.appointment-confirmation-panel__question {
  margin-bottom: 8px;
  color: #11291b;
  font-weight: 500;
}

.appointment-confirmation-panel__group,
.appointment-confirmation-panel__documents {
  margin-top: 18px;
}

.appointment-confirmation-panel__section-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 700;
}

.appointment-confirmation-panel__section-title:not(:first-child) {
  margin-top: 18px;
}

.appointment-confirmation-panel__section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: map.get($colors, 'orange');
  background: map.get($colors, 'white');
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.appointment-confirmation-panel__document-box {
  background: map.get($colors, 'secondary-3');
  border-radius: 4px;
  padding: 16px;
}

.appointment-confirmation-panel__file-label {
  color: map.get($colors, 'gray-6');
  font-size: 14px;
  margin-bottom: 6px;
}

.appointment-confirmation-panel__footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 18px 26px;
}

.appointment-confirmation-panel__button,
.appointment-confirmation-panel__button .btn,
.appointment-confirmation-panel__button button {
  width: 100%;
}

.appointment-confirmation-panel ::v-deep .custom-control {
  margin-bottom: 15px;
}

.appointment-confirmation-panel__radio ::v-deep .col-12 {
  padding-left: 0;
  padding-right: 0;
}

.appointment-confirmation-panel__file-input {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}

.appointment-confirmation-panel__file-input ::v-deep .col-12 {
  padding-left: 0;
  padding-right: 0;
}

.appointment-confirmation-panel__file-input ::v-deep .file-input-container {
  width: 100%;
  margin: 0;
}

@media (max-width: 576px) {
  .appointment-confirmation-panel__footer {
    grid-template-columns: 1fr;
  }
}
</style>
